import { useState, useEffect, useCallback, useMemo } from "react";
import axios from "axios";
import { toast } from "sonner";
import { Card, CardContent } from "../components/ui/card";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Badge } from "../components/ui/badge";
import {
  MapPin, Users, LogOut, RefreshCw, Bus, Download, Settings,
  ChevronRight, Plus, Trash2, CalendarDays, ArrowLeft,
} from "lucide-react";

const API = process.env.REACT_APP_BACKEND_URL + "/api";
const TOKEN_KEY = "trips_token";
const USER_KEY = "trips_user";

// ══════════════════════════════════════════════════════════════════════
// LOGIN
// ══════════════════════════════════════════════════════════════════════
function TripsLogin({ onLoggedIn }) {
  const [username, setUsername] = useState("");
  const [password, setPassword] = useState("");
  const [busy, setBusy] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    if (!username.trim() || !password) return;
    setBusy(true);
    try {
      const res = await axios.post(`${API}/trips/auth`, { username: username.trim(), password });
      localStorage.setItem(TOKEN_KEY, res.data.token);
      localStorage.setItem(USER_KEY, res.data.username);
      onLoggedIn(res.data.token, res.data.username);
    } catch (err) {
      toast.error(err.response?.data?.detail || "Login failed");
    } finally { setBusy(false); }
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-amber-50 via-orange-50 to-rose-50 flex items-center justify-center px-4">
      <Card className="w-full max-w-md border-2 border-amber-200 shadow-xl">
        <CardContent className="p-8 space-y-5">
          <div className="text-center space-y-2">
            <div className="w-14 h-14 rounded-2xl bg-amber-100 mx-auto flex items-center justify-center">
              <MapPin className="w-7 h-7 text-amber-700" />
            </div>
            <h1 className="text-2xl font-bold text-amber-900">Trips Dashboard</h1>
            <p className="text-sm text-amber-700/70">Daily roster · staff · bus estimates</p>
          </div>
          <form onSubmit={submit} className="space-y-3">
            <div>
              <label className="text-xs font-semibold text-amber-900 mb-1 block">Username</label>
              <Input value={username} onChange={(e) => setUsername(e.target.value)} placeholder="Bruno or Jon" autoFocus data-testid="trips-username-input" />
            </div>
            <div>
              <label className="text-xs font-semibold text-amber-900 mb-1 block">Password</label>
              <Input type="password" value={password} onChange={(e) => setPassword(e.target.value)} data-testid="trips-password-input" />
            </div>
            <Button type="submit" disabled={busy || !username.trim() || !password} className="w-full bg-amber-600 hover:bg-amber-700 text-white" data-testid="trips-login-btn">
              {busy ? "Signing in…" : "Access Trips"}
            </Button>
          </form>
        </CardContent>
      </Card>
    </div>
  );
}

// ══════════════════════════════════════════════════════════════════════
// SETTINGS TAB — Driver name management
// ══════════════════════════════════════════════════════════════════════
function TripsSettings({ token, drivers, reloadDrivers }) {
  const [newName, setNewName] = useState("");
  const [busy, setBusy] = useState(false);

  const addDriver = async (e) => {
    e.preventDefault();
    const name = newName.trim();
    if (!name) return;
    setBusy(true);
    try {
      await axios.post(`${API}/trips/drivers`, { name }, {
        headers: { Authorization: `Bearer ${token}` },
      });
      setNewName("");
      await reloadDrivers();
      toast.success(`Added ${name}`);
    } catch (err) {
      toast.error(err.response?.data?.detail || "Add failed");
    } finally { setBusy(false); }
  };

  const removeDriver = async (name) => {
    if (!window.confirm(`Remove driver "${name}"? All their bus assignments will be cleared.`)) return;
    try {
      await axios.delete(`${API}/trips/drivers`, {
        headers: { Authorization: `Bearer ${token}` },
        params: { name },
      });
      await reloadDrivers();
      toast.success(`Removed ${name}`);
    } catch (err) {
      toast.error(err.response?.data?.detail || "Remove failed");
    }
  };

  return (
    <div className="max-w-2xl mx-auto p-6 space-y-6">
      <div>
        <h2 className="text-2xl font-bold text-amber-900 mb-1">Drivers</h2>
        <p className="text-sm text-amber-700/70">
          Names here appear on every trip so you can assign a bus to each driver per trip.
        </p>
      </div>

      <Card className="border-2 border-amber-200">
        <CardContent className="p-4">
          <form onSubmit={addDriver} className="flex gap-2">
            <Input
              value={newName}
              onChange={(e) => setNewName(e.target.value)}
              placeholder="Add driver name…"
              data-testid="trips-driver-add-input"
            />
            <Button type="submit" disabled={busy || !newName.trim()} className="bg-amber-600 hover:bg-amber-700" data-testid="trips-driver-add-btn">
              <Plus className="w-4 h-4 mr-1" />Add
            </Button>
          </form>
        </CardContent>
      </Card>

      <div className="space-y-2">
        {drivers.length === 0 ? (
          <p className="text-center text-amber-700/60 py-10 text-sm">No drivers yet — add your first above.</p>
        ) : (
          drivers.map((d) => (
            <div
              key={d.name}
              className="flex items-center justify-between bg-white border border-amber-200 rounded-lg px-4 py-2.5"
              data-testid={`trips-driver-row-${d.name}`}
            >
              <span className="text-sm font-medium text-gray-900">{d.name}</span>
              <Button
                variant="ghost"
                size="sm"
                onClick={() => removeDriver(d.name)}
                className="text-red-600 hover:text-red-800 hover:bg-red-50 h-8"
                data-testid={`trips-driver-remove-${d.name}`}
              >
                <Trash2 className="w-4 h-4" />
              </Button>
            </div>
          ))
        )}
      </div>
    </div>
  );
}

// ══════════════════════════════════════════════════════════════════════
// TRIP CARD (per-trip driver assignment table)
// ══════════════════════════════════════════════════════════════════════
function TripCard({ token, day, trip, tripIndex, drivers, assignments, onAssignmentChange }) {
  const b = trip.buses || {};
  const busParts = [];
  if (b.buses_30) busParts.push(`${b.buses_30}× 30`);
  if (b.buses_19) busParts.push(`${b.buses_19}× 19`);
  const busLabel = busParts.join(" + ") || "—";

  // Map driver_name → bus_size from assignments (only entries matching this trip_index)
  const assignedFor = useMemo(() => {
    const m = {};
    for (const a of assignments) {
      if (a.trip_index === tripIndex) m[a.driver_name] = a.bus_size;
    }
    return m;
  }, [assignments, tripIndex]);

  const handleChange = async (driverName, busSize) => {
    try {
      await axios.put(
        `${API}/trips/assignments`,
        { date: day.date, trip_index: tripIndex, driver_name: driverName, bus_size: busSize || null },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      onAssignmentChange();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Save failed");
    }
  };

  // Roll-up of what's already assigned on this trip
  const assignedCount = { 30: 0, 19: 0 };
  Object.values(assignedFor).forEach(v => { if (v === "30" || v === "19") assignedCount[v]++; });

  return (
    <Card className="border-2 border-amber-200 shadow-sm" data-testid={`trip-card-${day.date}-${tripIndex}`}>
      <CardContent className="p-4 space-y-3">
        <div className="flex items-start justify-between border-b border-amber-100 pb-3">
          <div className="min-w-0 flex-1">
            <h3 className="text-lg font-bold text-gray-900">{trip.name}</h3>
            <p className="text-sm text-amber-700 font-medium">{trip.tag}</p>
          </div>
          <div className="flex items-center gap-2 ml-3">
            <Badge className="bg-amber-100 text-amber-800 border-amber-300">
              <Users className="w-3 h-3 mr-1" />{trip.campers ?? trip.expected}
            </Badge>
            <Badge className="bg-purple-100 text-purple-800 border-purple-300">+{trip.staff ?? 0} staff</Badge>
            <Badge className="bg-orange-100 text-orange-800 border-orange-300">
              <Bus className="w-3 h-3 mr-1" />{busLabel}
            </Badge>
          </div>
        </div>

        {drivers.length === 0 ? (
          <p className="text-sm text-amber-700/70 italic px-2 py-3">Add drivers in Settings to assign buses.</p>
        ) : (
          <div className="space-y-1.5">
            <div className="flex items-center justify-between text-[11px] font-semibold uppercase tracking-wide text-amber-800/70 px-1">
              <span>Driver</span>
              <span>
                Assigned this trip:
                {" "}
                <span className="text-orange-800">{assignedCount[30]}× 30</span>
                {" · "}
                <span className="text-orange-800">{assignedCount[19]}× 19</span>
              </span>
            </div>
            {drivers.map((d) => {
              const val = assignedFor[d.name] || "";
              return (
                <div
                  key={d.name}
                  className="flex items-center justify-between bg-white border border-amber-100 rounded-lg px-3 py-2"
                  data-testid={`trip-driver-row-${day.date}-${tripIndex}-${d.name}`}
                >
                  <span className="text-sm font-medium text-gray-900 truncate">{d.name}</span>
                  <select
                    value={val}
                    onChange={(e) => handleChange(d.name, e.target.value)}
                    className="text-sm border border-amber-300 rounded-md px-2 py-1 bg-white text-amber-900 focus:outline-none focus:ring-1 focus:ring-amber-500"
                    data-testid={`trip-driver-select-${day.date}-${tripIndex}-${d.name}`}
                  >
                    <option value="">No bus</option>
                    <option value="30">30-seat</option>
                    <option value="19">19-seat</option>
                  </select>
                </div>
              );
            })}
          </div>
        )}
      </CardContent>
    </Card>
  );
}

// ══════════════════════════════════════════════════════════════════════
// TRIPS DASHBOARD — day-sidebar + trip cards
// ══════════════════════════════════════════════════════════════════════
function TripsDashboard({ token, username, onLogout }) {
  const [days, setDays] = useState([]);
  const [loading, setLoading] = useState(true);
  const [drivers, setDrivers] = useState([]);
  const [activeTab, setActiveTab] = useState("trips");     // "trips" | "settings"
  const [activeDate, setActiveDate] = useState(null);
  const [assignments, setAssignments] = useState([]);
  const [downloadingDate, setDownloadingDate] = useState(null);
  const [showDayList, setShowDayList] = useState(false);   // mobile drawer

  const authHeaders = { headers: { Authorization: `Bearer ${token}` } };

  const loadBreakdown = useCallback(async () => {
    setLoading(true);
    try {
      const res = await axios.get(`${API}/trips/breakdown`, authHeaders);
      const list = res.data.days || [];
      setDays(list);
      if (list.length && !activeDate) setActiveDate(list[0].date);
    } catch (err) {
      if (err.response?.status === 401 || err.response?.status === 403) {
        toast.error("Session expired — sign in again");
        onLogout();
      } else {
        toast.error(err.response?.data?.detail || "Failed to load trips");
      }
    } finally { setLoading(false); }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [token]);

  const loadDrivers = useCallback(async () => {
    try {
      const res = await axios.get(`${API}/trips/drivers`, authHeaders);
      setDrivers(res.data.drivers || []);
    } catch (_e) { /* silent */ }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [token]);

  const loadAssignments = useCallback(async (date) => {
    if (!date) return;
    try {
      const res = await axios.get(`${API}/trips/assignments`, {
        ...authHeaders,
        params: { date },
      });
      setAssignments(res.data.assignments || []);
    } catch (_e) { setAssignments([]); }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [token]);

  useEffect(() => { loadBreakdown(); loadDrivers(); }, [loadBreakdown, loadDrivers]);
  useEffect(() => { if (activeDate) loadAssignments(activeDate); }, [activeDate, loadAssignments]);

  const activeDay = useMemo(() => days.find(d => d.date === activeDate) || null, [days, activeDate]);

  const downloadDay = async (date) => {
    setDownloadingDate(date);
    try {
      const res = await axios.get(`${API}/trips/day.pdf`, {
        ...authHeaders,
        params: { date },
        responseType: "blob",
      });
      const url = URL.createObjectURL(new Blob([res.data], { type: "application/pdf" }));
      const a = document.createElement("a");
      a.href = url;
      a.download = `trips_${date}.pdf`;
      a.click();
      URL.revokeObjectURL(url);
      toast.success(`Downloaded ${date}`);
    } catch (err) {
      toast.error(err.response?.data?.detail || "Download failed");
    } finally { setDownloadingDate(null); }
  };

  const fmtDayLabel = (d) => {
    const dt = new Date(d.date + "T12:00:00");
    return dt.toLocaleDateString("en-US", { weekday: "long", month: "short", day: "numeric" });
  };
  const fmtDayShort = (d) => {
    const dt = new Date(d.date + "T12:00:00");
    return dt.toLocaleDateString("en-US", { weekday: "short", month: "short", day: "numeric" });
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-amber-50 via-orange-50 to-rose-50">
      {/* ── HEADER ── */}
      <header className="sticky top-0 z-20 bg-white/95 backdrop-blur border-b border-amber-200 shadow-sm">
        <div className="max-w-7xl mx-auto px-4 py-3 flex items-center justify-between gap-2">
          <div className="flex items-center gap-2 min-w-0">
            <div className="w-9 h-9 rounded-xl bg-amber-100 flex items-center justify-center flex-shrink-0">
              <MapPin className="w-5 h-5 text-amber-700" />
            </div>
            <div className="min-w-0">
              <h1 className="text-lg font-bold text-amber-900 leading-tight truncate">Trips Dashboard</h1>
              <p className="text-[11px] text-amber-700/70 leading-tight">Signed in as {username}</p>
            </div>
          </div>
          <div className="flex items-center gap-1">
            <button
              type="button"
              onClick={() => setActiveTab("trips")}
              className={`px-3 py-1.5 rounded-lg text-sm font-medium transition ${
                activeTab === "trips" ? "bg-amber-600 text-white" : "text-amber-700 hover:bg-amber-100"
              }`}
              data-testid="trips-tab-trips"
            >
              Trips
            </button>
            <button
              type="button"
              onClick={() => setActiveTab("settings")}
              className={`px-3 py-1.5 rounded-lg text-sm font-medium transition flex items-center gap-1 ${
                activeTab === "settings" ? "bg-amber-600 text-white" : "text-amber-700 hover:bg-amber-100"
              }`}
              data-testid="trips-tab-settings"
            >
              <Settings className="w-3.5 h-3.5" /> Settings
            </button>
            <Button variant="ghost" size="sm" onClick={loadBreakdown} disabled={loading} className="text-amber-700 hover:text-amber-900 hover:bg-amber-100" data-testid="trips-refresh-btn">
              <RefreshCw className={`w-4 h-4 ${loading ? "animate-spin" : ""}`} />
            </Button>
            <Button variant="ghost" size="sm" onClick={onLogout} className="text-amber-700 hover:text-amber-900 hover:bg-amber-100" data-testid="trips-logout-btn">
              <LogOut className="w-4 h-4" />
            </Button>
          </div>
        </div>
      </header>

      {activeTab === "settings" ? (
        <TripsSettings token={token} drivers={drivers} reloadDrivers={loadDrivers} />
      ) : (
        <div className="max-w-7xl mx-auto flex">
          {/* ── SIDEBAR — day tabs ── */}
          <aside className={`w-56 border-r border-amber-200 bg-white/60 min-h-[calc(100vh-56px)] flex-shrink-0 ${showDayList ? "block" : "hidden md:block"}`}>
            <div className="p-3 border-b border-amber-200">
              <div className="flex items-center gap-1.5 text-xs font-bold uppercase tracking-wider text-amber-800">
                <CalendarDays className="w-3.5 h-3.5" /> Trip Days
              </div>
            </div>
            <nav className="p-2 space-y-1" data-testid="trips-day-sidebar">
              {days.map((d) => (
                <button
                  key={d.date}
                  type="button"
                  onClick={() => { setActiveDate(d.date); setShowDayList(false); }}
                  className={`w-full text-left px-3 py-2 rounded-lg transition flex items-center justify-between ${
                    d.date === activeDate
                      ? "bg-amber-600 text-white"
                      : "text-amber-900 hover:bg-amber-100"
                  }`}
                  data-testid={`trips-day-nav-${d.date}`}
                >
                  <div>
                    <div className="text-sm font-semibold leading-tight">{fmtDayShort(d)}</div>
                    <div className={`text-[10px] ${d.date === activeDate ? "text-amber-100" : "text-amber-700/70"}`}>
                      {d.trips.length} trip{d.trips.length !== 1 ? "s" : ""} · {d.day_total} ppl
                    </div>
                  </div>
                  <ChevronRight className="w-3.5 h-3.5 flex-shrink-0" />
                </button>
              ))}
            </nav>
          </aside>

          {/* ── MAIN — trips for the active day ── */}
          <main className="flex-1 min-w-0 p-4 md:p-6">
            {/* Mobile toggle for the day list */}
            <div className="md:hidden mb-3">
              <Button
                variant="outline"
                size="sm"
                onClick={() => setShowDayList(v => !v)}
                className="border-amber-300 text-amber-700"
                data-testid="trips-day-list-toggle"
              >
                <ArrowLeft className="w-4 h-4 mr-1" />{showDayList ? "Hide" : "Choose Day"}
              </Button>
            </div>

            {loading && days.length === 0 ? (
              <p className="text-center text-amber-700/70 py-20">Loading trips…</p>
            ) : !activeDay ? (
              <p className="text-center text-amber-700/70 py-20">Pick a day from the left.</p>
            ) : (
              <div className="space-y-4">
                <div className="flex items-center justify-between flex-wrap gap-3">
                  <div>
                    <h2 className="text-2xl font-bold text-amber-900">{fmtDayLabel(activeDay)}</h2>
                    <p className="text-sm text-amber-700/80">
                      {activeDay.day_total_campers ?? activeDay.day_total} campers ·
                      {" "}{activeDay.day_total_staff ?? 0} staff ·
                      {" "}est. {activeDay.fleet?.buses_total_used ?? 0} buses
                    </p>
                  </div>
                  <Button
                    size="sm"
                    variant="outline"
                    className="border-amber-300 text-amber-700 hover:bg-amber-50"
                    onClick={() => downloadDay(activeDay.date)}
                    disabled={downloadingDate === activeDay.date}
                    data-testid={`trips-download-${activeDay.date}`}
                  >
                    <Download className="w-4 h-4 mr-1" />
                    {downloadingDate === activeDay.date ? "…" : "Download PDF"}
                  </Button>
                </div>

                <div className="grid gap-4">
                  {activeDay.trips.map((t, i) => (
                    <TripCard
                      key={`${activeDay.date}-${i}`}
                      token={token}
                      day={activeDay}
                      trip={t}
                      tripIndex={i}
                      drivers={drivers}
                      assignments={assignments}
                      onAssignmentChange={() => loadAssignments(activeDay.date)}
                    />
                  ))}
                </div>
              </div>
            )}
          </main>
        </div>
      )}

      <footer className="max-w-7xl mx-auto px-4 pb-6 pt-2 text-center">
        <p className="text-[11px] text-amber-700/50">
          Counts from daily roster · CIT counts from CampMinder trip yes/no · Fleet: 4× 30-seat + 12× 19-seat.
        </p>
      </footer>
    </div>
  );
}

// ══════════════════════════════════════════════════════════════════════
// PAGE ROOT
// ══════════════════════════════════════════════════════════════════════
export default function TripsPage() {
  const [token, setToken] = useState(() => localStorage.getItem(TOKEN_KEY));
  const [username, setUsername] = useState(() => localStorage.getItem(USER_KEY) || "");

  const handleLoggedIn = (t, u) => { setToken(t); setUsername(u); };
  const handleLogout = () => {
    localStorage.removeItem(TOKEN_KEY);
    localStorage.removeItem(USER_KEY);
    setToken(null); setUsername("");
  };

  if (!token) return <TripsLogin onLoggedIn={handleLoggedIn} />;
  return <TripsDashboard token={token} username={username} onLogout={handleLogout} />;
}
